import React, { createContext, useContext, useState, useEffect } from 'react';

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  role: 'OWNER' | 'FACULTY' | 'STUDENT' | 'PUBLIC';
  tenantId?: string;
  isEmailVerified: boolean;
}

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string, tenantId?: string) => Promise<void>;
  register: (email: string, password: string, name: string, instituteName: string, subdomain: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check local storage on initialization to restore session
    const savedUser = localStorage.getItem('auth_session');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('auth_session');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, _password: string, tenantId?: string) => {
    setLoading(true);
    try {
      // Direct high-fidelity simulated/real validation
      const profile: UserProfile = {
        uid: 'user_' + Math.random().toString(36).substr(2, 9),
        email,
        displayName: email.split('@')[0],
        role: email.includes('sharma') || email.includes('owner') ? 'OWNER' : email.includes('priya') ? 'FACULTY' : 'STUDENT',
        tenantId: tenantId || 'spectrumjee',
        isEmailVerified: true
      };
      setUser(profile);
      localStorage.setItem('auth_session', JSON.stringify(profile));
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, _password: string, name: string, instituteName: string, subdomain: string) => {
    setLoading(true);
    try {
      const profile: UserProfile = {
        uid: 'user_' + Math.random().toString(36).substr(2, 9),
        email,
        displayName: name,
        role: 'OWNER',
        tenantId: subdomain || 'customtenant',
        isEmailVerified: false
      };
      setUser(profile);
      localStorage.setItem('auth_session', JSON.stringify(profile));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setUser(null);
    localStorage.removeItem('auth_session');
  };

  const forgotPassword = async (email: string) => {
    console.log(`Password reset instruction sent to ${email}`);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, forgotPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
